import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, IconButton, Link, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { Add, Close } from '@mui/icons-material';

import { CaseSupportFormResponseDto } from '@bandido/shared/dtos';

import { Table, TableLoader, usePagination } from '@frontend/features/common/components/Table';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { NEW_SUPPORT_FORM_ID, useGetSupportFormsByAgency } from '@frontend/features/agencies/queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { DeleteSupportForm } from '@frontend/features/support-forms/components/DeleteSupportForm';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';

import { routes } from '../routes';

export const SuportFormList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const pagination = usePagination();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { id: agencyId } = useParams();
  const [search, setSearch] = useState('');
  const [selectedForm, setSelectedForm] = useState<CaseSupportFormResponseDto | undefined>(undefined);

  const { data: forms, isLoading: formsLoading } = useGetSupportFormsByAgency({
    agencyId,
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search,
    onErrorCallback: () => {
      addToast({
        color: 'error',
        message: t('supportForms.list.error'),
      });
    },
  });

  const isLoading = false;

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  const handleNavigateToEditForm = (id?: string) => {
    navigate(
      `${routes.dashboard.agencyView.root.replace(
        ':id',
        String(agencyId),
      )}/${routes.dashboard.agencyView.supportFormEdit.replace(':supportFormId', id || NEW_SUPPORT_FORM_ID)}`,
    );
  };

  const handleNavigateToWorkflow = (id: string) => {
    navigate(
      `${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/${
        routes.dashboard.agencyView.workflows
      }/${id}`,
    );
  };

  const handleNavigateToExternalForm = (id: string) => {
    window.open(`${routes.external.caseSupportForms.replace(':id', String(id))}`, '_blank', 'rel=noopener noreferrer');
  };

  if (!agencyId) {
    return null;
  }

  return (
    <>
      <ListLayout padding={0} title={t('supportForms.list.title')} subtitle={t('supportForms.list.subtitle')}>
        <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
          <Typography variant="body1" flex={1} color={theme.palette.text.primary}>
            {isLoading ? (
              <Skeleton
                sx={{
                  backgroundColor: theme.palette.field.main,
                  maxWidth: theme.spacing(10),
                }}
              />
            ) : (
              t('common.tableList.numberOfResults', { count: forms?.count || 0 })
            )}
          </Typography>

          <SearchInput placeholder="Buscar" onSearch={handleOnSearch} />

          <ToggleButtonGroup
            exclusive
            sx={{
              height: theme.spacing(5),
              '& button': {
                padding: theme.spacing(1),
                width: theme.spacing(5),
              },
            }}
          >
            <ToggleButton onClick={() => handleNavigateToEditForm()} value="add" aria-label="right aligned">
              <Add />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {formsLoading && (
          <TableLoader
            sx={{
              backgroundColor: theme.palette.field.main,
              paddingY: theme.spacing(2),
            }}
            rows={3}
          />
        )}
        {!formsLoading && (
          <Table
            height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
            columns={[
              {
                id: 'title',
                label: t('supportForms.fields.titleField'),
                align: 'left',
                format: (_, form) => (
                  <Link component="button" onClick={() => handleNavigateToEditForm(form.id)}>
                    {form.title}
                  </Link>
                ),
              },
              {
                id: 'workflow',
                label: t('supportForms.fields.workflowField'),
                align: 'left',
                format: (_, form) => (
                  <Link component="button" onClick={() => handleNavigateToWorkflow(form.workflowId)}>
                    {form.workflowName}
                  </Link>
                ),
              },
              {
                id: 'finalUrl',
                label: t('supportForms.fields.finalUrl'),
                align: 'left',
                format: (_, form) => (
                  <Link component="button" onClick={() => handleNavigateToExternalForm(form.id)}>
                    {`${routes.external.caseSupportForms.replace(':id', String(form.id))}`}
                  </Link>
                ),
              },
              {
                id: 'actions',
                label: '',
                align: 'left',
                format: (_, form) => (
                  <IconButton onClick={() => setSelectedForm(form)}>
                    <Close color="error" />
                  </IconButton>
                ),
              },
            ]}
            data={{
              key: 'id',
              items: forms?.items || [],
              totalCount: forms?.count || 0,
            }}
            pagination={pagination}
            stickyHeader
          />
        )}

        {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
      </ListLayout>
      <DeleteSupportForm
        id={selectedForm?.id}
        name={selectedForm?.title}
        open={Boolean(selectedForm)}
        onClose={() => setSelectedForm(undefined)}
      />
    </>
  );
};
